(() => {
  "use strict";

  if (customElements.get("typing-test")) return;

  class TypingTest extends HTMLElement {
    constructor() {
      super();
      this._state = "idle";
      this._words = [];
      this._wordIndex = 0;
      this._charIndex = 0;
      this._correctChars = 0;
      this._wpm = 0;
      this._startTime = null;
      this._timer = null;
      this._scrollOffset = 0;
      this._duration = 25;
      this._targetWpm = 80;
      this._textBody = "";
      this._winMessage = "";
      this._loseMessage = "";
      this._wordEls = [];
      this._blinkTimeout = null;
    }

    connectedCallback() {
      const configEl = this.querySelector("script[data-typing-config]");
      if (configEl) {
        try {
          const config = JSON.parse(configEl.textContent);
          this._duration = config.duration || 25;
          this._targetWpm = config.targetWpm || 80;
          this._textBody = config.textBody || "";
          this._winMessage = config.winMessage || "You win!";
          this._loseMessage = config.loseMessage || "Not fast enough.";
        } catch (e) {
          console.error("TypingTest: Failed to parse config", e);
        }
      }

      this._els = {
        idle: this.querySelector("[data-idle-area]"),
        game: this.querySelector("[data-game-area]"),
        result: this.querySelector("[data-result-area]"),
        words: this.querySelector("[data-words]"),
        wordsInner: this.querySelector("[data-words-inner]"),
        wpm: this.querySelector("[data-wpm]"),
        progressBar: this.querySelector("[data-progress-bar]"),
        time: this.querySelector("[data-time]"),
        input: this.querySelector("[data-typing-input]"),
        startBtn: this.querySelector("[data-start-btn]"),
        restartBtn: this.querySelector("[data-restart-btn]"),
        resultMsg: this.querySelector("[data-result-message]"),
        resultWpm: this.querySelector("[data-result-wpm]"),
        focusHint: this.querySelector("[data-focus-hint]"),
        typingArea: this.querySelector("[data-typing-area]"),
      };

      this._els.startBtn?.addEventListener("click", () => this._start());
      this._els.restartBtn?.addEventListener("click", () => this._start());

      if (this._els.input) {
        // Use keydown only for control keys (Backspace, Tab)
        this._els.input.addEventListener("keydown", (e) => this._onKeydown(e));
        // Use input event for actual character processing — more reliable
        // across browsers, IME, and keyboard layouts than keydown + e.key
        this._els.input.addEventListener("input", (e) => this._onInput(e));
        this._els.input.addEventListener("focus", () => this._onFocus());
        this._els.input.addEventListener("blur", () => this._onBlur());
      }

      this._els.typingArea?.addEventListener("click", () => {
        if (this._state === "playing") this._els.input?.focus();
      });
    }

    _start() {
      this._state = "playing";
      this._wordIndex = 0;
      this._charIndex = 0;
      this._correctChars = 0;
      this._wpm = 0;
      this._startTime = null;
      this._scrollOffset = 0;
      clearInterval(this._timer);

      // Sanitize text: strip zero-width chars and normalize whitespace
      const cleaned = this._textBody
        .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, " ")
        .trim();
      const rawWords = cleaned.split(/\s+/).filter((w) => w.length > 0);
      if (rawWords.length === 0) return;

      this._words = [];
      while (this._words.length < 200) {
        this._words.push(...rawWords);
      }

      this._els.idle?.classList.add("hidden");
      this._els.result?.classList.add("hidden");
      this._els.game?.classList.remove("hidden");

      this._renderWords();
      this._els.wpm.textContent = "0";
      this._els.time.textContent = String(this._duration);
      this._els.progressBar.style.width = "100%";

      this._els.input.value = "";
      requestAnimationFrame(() => this._els.input?.focus());
    }

    _renderWords() {
      const inner = this._els.wordsInner;
      inner.innerHTML = "";
      inner.style.transform = "translateY(0)";
      this._scrollOffset = 0;
      this._wordEls = [];

      this._words.forEach((word, wi) => {
        const wordSpan = document.createElement("span");
        wordSpan.className = "typing-word";

        const charEls = [];
        for (const ch of word) {
          const charSpan = document.createElement("span");
          charSpan.textContent = ch;
          charSpan.className = "typing-char";
          wordSpan.appendChild(charSpan);
          charEls.push(charSpan);
        }

        inner.appendChild(wordSpan);
        this._wordEls.push({ el: wordSpan, chars: charEls });

        if (wi < this._words.length - 1) {
          inner.appendChild(document.createTextNode(" "));
        }
      });

      this._updateCursor();
    }

    _onKeydown(e) {
      if (this._state !== "playing") return;

      if (e.key === "Tab") {
        e.preventDefault();
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
        this._backspace();
        this._flashCursor();
      }
    }

    _onInput(e) {
      if (this._state !== "playing") return;

      const data = e.data;
      if (!data) {
        // Deletion or non-text input — clear and bail
        this._els.input.value = "";
        return;
      }

      // Start timer on first real input
      if (!this._startTime) {
        this._startTime = performance.now();
        this._startTimer();
      }

      // Process each character from the input event
      for (const char of data) {
        if (char === " ") {
          this._space();
        } else {
          this._type(char);
        }
      }

      this._flashCursor();
      // Keep the hidden input clean so it doesn't accumulate text
      this._els.input.value = "";
    }

    _type(char) {
      if (this._wordIndex >= this._words.length) return;

      const word = this._words[this._wordIndex];
      if (this._charIndex >= word.length) return;

      const expected = word[this._charIndex];
      const charEl = this._wordEls[this._wordIndex].chars[this._charIndex];

      if (char === expected) {
        charEl.classList.add("correct");
        this._correctChars++;
      } else {
        charEl.classList.add("incorrect");
      }

      this._charIndex++;
      this._updateCursor();
      this._calcWpm();
    }

    _space() {
      if (this._wordIndex >= this._words.length) return;
      if (this._charIndex === 0) return;

      const word = this._words[this._wordIndex];
      const wordEl = this._wordEls[this._wordIndex];
      for (let i = this._charIndex; i < word.length; i++) {
        wordEl.chars[i].classList.add("skipped");
      }

      this._correctChars++;
      this._wordIndex++;
      this._charIndex = 0;

      if (this._wordIndex >= this._words.length) {
        this._finish();
        return;
      }

      this._updateCursor();
      this._scrollToWord();
      this._calcWpm();
    }

    _backspace() {
      if (this._charIndex > 0) {
        this._charIndex--;
        const charEl = this._wordEls[this._wordIndex].chars[this._charIndex];
        if (charEl.classList.contains("correct")) this._correctChars--;
        charEl.classList.remove("correct", "incorrect", "skipped");
        this._updateCursor();
        this._calcWpm();
      }
    }

    _updateCursor() {
      this._els.wordsInner.querySelector(".typing-cursor")?.remove();

      if (this._wordIndex >= this._wordEls.length) return;

      const wordEl = this._wordEls[this._wordIndex];
      const cursor = document.createElement("span");
      cursor.className = "typing-cursor";

      if (this._charIndex < wordEl.chars.length) {
        wordEl.el.insertBefore(cursor, wordEl.chars[this._charIndex]);
      } else {
        wordEl.el.appendChild(cursor);
      }
    }

    _flashCursor() {
      const cursor = this._els.wordsInner.querySelector(".typing-cursor");
      if (!cursor) return;
      cursor.classList.add("active");
      clearTimeout(this._blinkTimeout);
      this._blinkTimeout = setTimeout(() => {
        cursor.classList.remove("active");
      }, 500);
    }

    _scrollToWord() {
      if (this._wordIndex >= this._wordEls.length) return;

      const wordEl = this._wordEls[this._wordIndex].el;
      const lineHeight = parseFloat(
        getComputedStyle(this._els.wordsInner).lineHeight
      );
      if (lineHeight <= 0) return;

      const wordTop = wordEl.offsetTop;

      if (wordTop > this._scrollOffset + lineHeight * 0.5) {
        this._scrollOffset = wordTop;
        this._els.wordsInner.style.transform = `translateY(-${wordTop}px)`;
      }
    }

    _startTimer() {
      this._timer = setInterval(() => {
        const elapsed = (performance.now() - this._startTime) / 1000;
        const remaining = Math.max(0, this._duration - elapsed);

        const stepped = Math.ceil(remaining) / this._duration;
        this._els.progressBar.style.width = `${stepped * 100}%`;
        this._els.time.textContent = String(Math.ceil(remaining));

        this._calcWpm();

        if (remaining <= 0) {
          this._finish();
        }
      }, 100);
    }

    _calcWpm() {
      if (!this._startTime) return;

      const minutes = (performance.now() - this._startTime) / 60000;
      if (minutes <= 0) return;

      this._wpm = Math.round(this._correctChars / 5 / minutes);
      this._els.wpm.textContent = String(this._wpm);
    }

    _finish() {
      this._state = "result";
      clearInterval(this._timer);

      const won = this._wpm >= this._targetWpm;

      this._els.game?.classList.add("hidden");
      this._els.result?.classList.remove("hidden");

      this._els.resultMsg.textContent = won
        ? this._winMessage
        : this._loseMessage;
      this._els.resultWpm.textContent = String(this._wpm);
      this._els.result.dataset.outcome = won ? "win" : "lose";
    }

    _onFocus() {
      this._els.focusHint?.classList.add("hidden");
    }

    _onBlur() {
      if (this._state === "playing") {
        this._els.focusHint?.classList.remove("hidden");
      }
    }

    disconnectedCallback() {
      clearInterval(this._timer);
      clearTimeout(this._blinkTimeout);
    }
  }

  customElements.define("typing-test", TypingTest);
})();
